import { Injectable } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Logger } from "aws-amplify";
import {
    ContentService,
    MatterportService,
    VisitTemplate,
} from "@smarterplan/ngx-smarterplan-core";
import { Subject } from "rxjs";
import { MuseumTagService } from "../museum/museum-tag.service";
import { PhotonClient, waitUntil } from "./PhotonClient";
import { LocalSimulator } from "./simulator/local-simulator";
import { RemoteSimulator } from "./simulator/remote-simulator";
import { Actor } from "./types";

@Injectable({ providedIn: "root" })
export class RemoteControlService {
    sdk: any;

    photonClient: PhotonClient;

    hostSimulator: LocalSimulator;

    clientSimulator: RemoteSimulator;

    private sessionName = "Remote123";

    private room: Photon.LoadBalancing.Room = null;

    public userCount: Subject<number> = new Subject();

    public onConnectionChange: Subject<boolean> = new Subject();

    public currentTagOnlineChange: Subject<string> = new Subject();

    liveTour: VisitTemplate;

    logger: Logger;

    loggerInfo: { name: string; tourID: string; };

    public currentTagOnline: string;

    constructor(
        private matterportService: MatterportService,
        private contentService: ContentService,
        private modalService: NgbModal,
        private tagService: MuseumTagService,
    ) {
        this.photonClient = new PhotonClient();
        this.currentTagOnlineChange.subscribe((id) => {
            this.currentTagOnline = id;
        });
    }

    setSdk(
        sdkinject: any,
        liveTour: VisitTemplate = null,
        isGuide: boolean = false,
    ) {
        this.liveTour = liveTour;
        this.sdk = sdkinject;
        this.setSimulators(isGuide);
    }

    setSimulators(isGuide: boolean) {
        if (isGuide) {
            this.hostSimulator = new LocalSimulator(
                this.photonClient,
                this.sdk,
                this.matterportService,
                this.contentService,
                this.liveTour,
                this.modalService,
                this.tagService,
                this,
            );
            this.hostSimulator.setLogger(this.logger, this.loggerInfo);
        } else {
            this.clientSimulator = new RemoteSimulator(
                this.photonClient,
                this.sdk,
                this.matterportService,
                this.contentService,
                this,
            );
            this.clientSimulator.setLogger(this.logger, this.loggerInfo);
        }
    }

    setMatterportService(matterportService: MatterportService) {
        this.matterportService = matterportService;
    }

    setLogger(logger: Logger, loggerInfo: { name: string; tourID: string; }) {
        this.logger = logger;
        this.loggerInfo = loggerInfo;
    }

    async connectHost(sessionName: string): Promise<boolean> {
        await this.photonClient.start();
        this.sessionName = sessionName;

        for (const room of this.photonClient.availableRooms()) {
            if (
                room.name === this.sessionName &&
                room.masterClientId !== this.photonClient.myActor().actorNr
            ) {
                this.logger.warn(
                    `${JSON.stringify(
                        this.loggerInfo,
                    )}: **PHOTON** - Client isn't room owner, cannot host`,
                );
                this.photonClient.disconnect();
                return false;
            }
        }

        this.photonClient.joinRoom(this.sessionName, {
            createIfNotExists: true,
        });

        await waitUntil(() => this.photonClient.isJoinedToRoom());

        const room = this.photonClient.myRoom();
        this.logger.info(
            `${JSON.stringify(
                this.loggerInfo,
            )}: **PHOTON** - Host started room ${room.name}`,
        );
        room.setCustomProperty("host", this.photonClient.myActor().actorNr);
        room.getCustomPropertyOrElse(
            "host",
            this.photonClient.myActor().actorNr,
        );
        this.room = room;
        this.userCount.next(this.room.playerCount);
        this.onConnectionChange.next(true);
        this.photonClient.events.addListener(
            "onActorJoin",
            function onJoin() {
                this.userCount.next(this.room.playerCount);
            }.bind(this),
        );
        this.photonClient.events.addListener(
            "onActorLeave",
            function onLeave() {
                this.userCount.next(this.room.playerCount);
            }.bind(this),
        );
        this.hostSimulator.start();
        return true;
    }

    /**
     * Connects user to the room if it exists.
     * @param sessionName name of the room to connect to
     * @returns number of users in joined room
     */
    async connectToRoom(sessionName: string): Promise<number> {
        await this.photonClient.start();
        // very important to start simulator immediately, otherwise we loose event in cache
        this.clientSimulator.start();
        // no interaction with visit
        const iframe = document.querySelector(
            "#viewer-module",
        ) as HTMLIFrameElement;
        const preventVisitorClick = document.querySelector(
            ".prevent-visitor-click",
        ) as HTMLDivElement;
        if (iframe) {
            iframe.style.pointerEvents = "none";
            preventVisitorClick.style.display = "block";
        }
        this.sessionName = sessionName;
        // const rooms = this.photonClient.availableRooms();
        // if (rooms.length === 0) {
        //     alert(this.translate.instant("remote.host.late"));
        //     this.photonClient.disconnect();
        //     return null;
        // }
        // const roomNames = rooms.map((room) => room.name);
        // if (!roomNames.includes(sessionName)) {
        //     alert(
        //         `${sessionName}: ${this.translate.instant(
        //             "remote.no-session",
        //         )}`,
        //     );
        //     this.photonClient.disconnect();
        //     return null;
        // }
        this.photonClient.joinRoom(this.sessionName);
        await waitUntil(() => this.photonClient.isJoinedToRoom());

        const room = this.photonClient.myRoom();
        this.logger.info(
            `${JSON.stringify(this.loggerInfo)}: **PHOTON** - Joined room ${room.name
            }`,
        );
        this.onConnectionChange.next(true);
        this.room = room;
        this.photonClient.events.addListener(
            "onActorJoin",
            function onJoin() {
                this.userCount.next(this.room.playerCount);
            }.bind(this),
        );
        this.photonClient.events.addListener(
            "onActorLeave",
            this.onActorLeave.bind(this),
        );
        return this.room.playerCount;
    }

    onStopHosting() {
        this.room = null;
        this.photonClient.events.removeListener("onActorLeave");
        this.photonClient.events.removeListener("onActorJoin");
        // clean all properties
        this.photonClient.myActor().setCustomProperty("pose", null);
        this.photonClient.myActor().setCustomProperty("nextSlide", null);
        this.photonClient.myActor().setCustomProperty("closeModal", null);
        this.photonClient.myActor().setCustomProperty("tagAction", null);
        this.photonClient.myActor().setCustomProperty("tag", null);
        this.photonClient.disconnect();
        this.onConnectionChange.next(false);
        if (this.hostSimulator) {
            this.hostSimulator.stop();
        }
        this.hostSimulator = null;
        this.logger.info(
            `${JSON.stringify(
                this.loggerInfo,
            )}: **PHOTON** - Remote stopped hosting`,
        );
    }

    leaveRoom() {
        this.room = null;
        this.clientSimulator.stop();
        this.onConnectionChange.next(false);
        this.photonClient.disconnect();
        this.clientSimulator = null;
        this.logger.info(
            `${JSON.stringify(this.loggerInfo)}: **PHOTON** - Left room`,
        );
        // activate interaction with iframe
        const iframe = document.querySelector(
            "#viewer-module",
        ) as HTMLIFrameElement;
        const preventVisitorClick = document.querySelector(
            ".prevent-visitor-click",
        ) as HTMLDivElement;
        if (iframe) {
            iframe.style.pointerEvents = "auto";
            preventVisitorClick.style.display = "none";
        }
    }

    private onActorLeave(actor: Actor) {
        // cant call leaveRoom during onActorLeave callback.
        const host: number = this.room.getCustomProperty("host");

        if (actor.id === host) {
            this.logger.debug(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: **PHOTON** - Host left, leaving room`,
            );
            this.leaveRoom();
            this.userCount.next(null);
        } else {
            this.userCount.next(this.room.playerCount);
        }
    }
}
